import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { styled } from '@mui/system';

import Grid from '@mui/material/Grid';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import DarkCover from 'components/DarkCover';
import CardHeader from 'bento-ordering/menus/ui_components/CardHeader';
import Card from 'bento-ordering/menus/ui_components/Card';
import Calories from 'bento-ordering/menus/components/Calories';
import Dotdotdot from 'react-dotdotdot';
import { getImageWithHeight } from 'bento-ordering/utils/images';
import { getMenuItemNamesLightColor } from 'styles/helpers/getThemeStyles';

import { getItemDetailString } from 'bento-ordering/menus/utils/getItemDetailString';
import muiPropForwarding from 'styles/helpers/muiPropForwarding';
import MenuItemNameTypography from 'components/MenuItemNameTypography';
import MenuItemDescriptionTypography from 'components/MenuItemDescriptionTypography';
import ownCardState from 'helpers/ownState/DropShadowState';
import { getPrice, getCalories } from 'helpers/menuItemCardHelper';
import useProductHasMultipleSizes from 'hooks/useProductHasMultipleSizes';
import { ITEM_DESCRIPTION_AND_PRICES } from 'styles/constants/appearanceSectionNames';
import isCateringRoute from 'catering/helpers/isCateringRoute';

const SoldOutTypography = styled(Typography)(({ theme }) => {
  return {
    color: theme.palette.white
  };
});

const StyledCardMedia = styled(CardMedia, {
  shouldForwardProp: muiPropForwarding('focalPointX', 'focalPointY')
})(({ focalPointX, focalPointY }) => ({
  position: 'absolute',
  height: '100%',
  width: '100%',
  backgroundPosition:
    focalPointX && focalPointY
      ? `${focalPointX * 100}% ${focalPointY * 100}%`
      : 'initial'
}));

const ImageContainer = styled(Grid)(({ theme }) => ({
  position: 'relative',
  order: 0,
  minHeight: '172px',
  [theme.breakpoints.down('md')]: {
    order: -1
  }
}));

const Description = styled('div')({
  marginTop: 6
});

const Detail = styled('div')({
  marginTop: 4
});

const TextContent = styled('div')({
  marginTop: 8
});

const LeadTime = styled('div')({
  display: 'flex',
  marginLeft: -1,
  marginTop: 4
});

const PriceText = styled(MenuItemNameTypography)({
  fontStyle: 'normal',
  fontSize: '14px'
});

const LeadTimeText = styled(MenuItemDescriptionTypography)({
  marginLeft: 4
});

const DescriptionText = styled(MenuItemDescriptionTypography)({
  fontSize: '14px'
});

const StyledScheduleIcon = styled(ScheduleIcon)(({ theme }) => {
  return {
    width: '.66em',
    height: 'auto',
    fill: getMenuItemNamesLightColor(theme)
  };
});

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'space-between',
  [theme.breakpoints.down('md')]: {
    display: 'block'
  }
}));

const StyledGrid = styled(Grid)({
  height: '100%'
});

const StyledGridContainer = styled(Grid)(({ theme }) => {
  const styles = {
    height: '100%',
    backgroundColor: theme.palette.card.background.main
  };
  return {
    '&.MuiGrid-container': styles
  };
});

const getTypeVariant = isSoldOut => (isSoldOut ? 'body2' : 'body1');

const MenuItemCard = ({
  name,
  description,
  image,
  calories,
  price,
  inventoryLessCart,
  handleClick,
  inventory,
  leadTimeText,
  minimumQuantity,
  quantityHandlingMethod,
  variants,
  lowInventoryCount,
  hasInvestorDayFeature,
  productHasSurchargeModifier = false,
  // DIET TYPE OPTIONS
  contains_sugar,
  dairy_free_option,
  halal,
  keto,
  keto_option,
  kosher,
  kosher_option,
  low_fodmap,
  low_sodium,
  non_alcoholic,
  organic,
  organic_option,
  paleo,
  paleo_option,
  plant_based,
  plant_based_option,
  spicy,
  spicy_hot,
  spicy_medium,
  spicy_mild,
  sugar_free,
  uncooked,
  vegan,
  vegan_option,
  vegetarian,
  vegetarian_option,
  // ALLERGEN OPTIONS
  cooked_to_order,
  corn,
  corn_free,
  dairy,
  dairy_free,
  egg,
  egg_free,
  fish,
  fish_free,
  gluten,
  gluten_free,
  nuts,
  nuts_free,
  peanut,
  peanut_free,
  raw,
  raw_egg,
  sesame,
  sesame_free,
  shellfish,
  shellfish_free,
  soy,
  soy_free,
  tree_nut,
  tree_nut_free,
  undercooked,
  wheat,
  wheat_free
}) => {
  const isCatering = isCateringRoute();
  const productHasMultipleSizes = useProductHasMultipleSizes({ variants });

  const detail = getItemDetailString([
    { 'Contains Sugar': contains_sugar },
    { 'Dairy Free Option': dairy_free_option },
    { Halal: halal },
    { Keto: keto },
    { 'Keto Option': keto_option },
    { Kosher: kosher },
    { 'Kosher Option': kosher_option },
    { 'Low FODMAP': low_fodmap },
    { 'Low Sodium': low_sodium },
    { 'Non-Alcoholic': non_alcoholic },
    { Organic: organic },
    { 'Organic Option': organic_option },
    { Paleo: paleo },
    { 'Paleo Option': paleo_option },
    { 'Plant Based': plant_based },
    { 'Plant Based Option': plant_based_option },
    { Spicy: spicy },
    { Hot: spicy_hot },
    { Medium: spicy_medium },
    { Mild: spicy_mild },
    { 'Sugar Free': sugar_free },
    { Uncooked: uncooked },
    { Vegan: vegan },
    { 'Vegan Option': vegan_option },
    { Vegetarian: vegetarian },
    { 'Vegetarian Option': vegetarian_option }
  ]);

  const allergenDetail = getItemDetailString([
    { 'Contains Raw Egg': raw_egg },
    { 'Cooked to Order': cooked_to_order },
    { Corn: corn },
    { 'Corn Free': corn_free },
    { Dairy: dairy },
    { 'Dairy Free': dairy_free },
    { Egg: egg },
    { 'Egg Free': egg_free },
    { Fish: fish },
    { 'Fish Free': fish_free },
    { Gluten: gluten },
    { 'Gluten Free': gluten_free },
    { Nuts: nuts },
    { 'Nuts Free': nuts_free },
    { Peanut: peanut },
    { 'Peanut Free': peanut_free },
    { Raw: raw },
    { Sesame: sesame },
    { 'Sesame Free': sesame_free },
    { Shellfish: shellfish },
    { 'Shellfish Free': shellfish_free },
    { Soy: soy },
    { 'Soy Free': soy_free },
    { 'Tree Nut': tree_nut },
    { 'Tree Nut Free': tree_nut_free },
    { Undercooked: undercooked },
    { Wheat: wheat },
    { 'Wheat Free': wheat_free }
  ]);

  const isSoldOut = inventory === null ? false : inventory <= 0;

  const remainingInventoryIsInCart =
    !isSoldOut && inventoryLessCart !== null && inventoryLessCart <= 0;

  const typeVariant = getTypeVariant(isSoldOut || remainingInventoryIsInCart);

  const itemDescriptionAndDetailsStyles = {
    variant: ITEM_DESCRIPTION_AND_PRICES
  };

  const priceStyles = { variant: ITEM_DESCRIPTION_AND_PRICES };

  const textContentGridStyles = image.url
    ? { lg: 8, md: 7, xs: 12 }
    : { xs: 12 };

  const resizedImage = getImageWithHeight(image.url, 520);

  const shouldDisplayInventoryLeftMessage =
    !!inventoryLessCart &&
    inventoryLessCart <= lowInventoryCount &&
    inventoryLessCart > 0;

  return (
    <Card
      disabled={!isCatering && (isSoldOut || remainingInventoryIsInCart)}
      handleClick={handleClick}
      dataTestId="menu-page-item"
      ownState={ownCardState()}
    >
      <StyledGridContainer container>
        <StyledGrid item {...textContentGridStyles}>
          <CardContent>
            <StyledBox>
              <div>
                <CardHeader title={name} disabled={isSoldOut && !isCatering} />
                {description && (
                  <Description>
                    {/* Typography component set as span to avoid errors of
                    div as descendant of p */}
                    <DescriptionText
                      component="span"
                      {...itemDescriptionAndDetailsStyles}
                    >
                      <Dotdotdot clamp={2}>{description}</Dotdotdot>
                    </DescriptionText>
                  </Description>
                )}
                {allergenDetail && (
                  <Detail>
                    <MenuItemDescriptionTypography
                      component="p"
                      {...itemDescriptionAndDetailsStyles}
                    >
                      {allergenDetail}
                    </MenuItemDescriptionTypography>
                  </Detail>
                )}
                {detail && (
                  <Detail>
                    <MenuItemDescriptionTypography
                      component="p"
                      {...itemDescriptionAndDetailsStyles}
                    >
                      {detail}
                    </MenuItemDescriptionTypography>
                  </Detail>
                )}
              </div>
              <TextContent>
                {!!calories && (
                  <Calories
                    calories={getCalories(
                      calories,
                      productHasMultipleSizes,
                      minimumQuantity
                    )}
                    typeVariant={typeVariant}
                  />
                )}
                {!hasInvestorDayFeature && (
                  <PriceText component="p" {...priceStyles}>
                    {getPrice(
                      remainingInventoryIsInCart,
                      isSoldOut,
                      price,
                      minimumQuantity,
                      quantityHandlingMethod,
                      productHasMultipleSizes,
                      productHasSurchargeModifier
                    )}
                    {shouldDisplayInventoryLeftMessage && (
                      <MenuItemDescriptionTypography
                        {...priceStyles}
                        data-testid="menu-item-inventory-left"
                        component="span"
                      >
                        {` - only ${inventoryLessCart} items left`}
                      </MenuItemDescriptionTypography>
                    )}
                    {!!minimumQuantity && minimumQuantity > 1 && (
                      <MenuItemDescriptionTypography
                        data-testid="minimum-quantity-text"
                        component="span"
                        {...priceStyles}
                      >
                        {` (Min. ${minimumQuantity})`}
                      </MenuItemDescriptionTypography>
                    )}
                  </PriceText>
                )}
                {leadTimeText && (
                  <LeadTime>
                    <StyledScheduleIcon />
                    <LeadTimeText
                      {...priceStyles}
                      data-testid="extra-lead-time"
                      component="p"
                    >
                      {leadTimeText}
                    </LeadTimeText>
                  </LeadTime>
                )}
              </TextContent>
            </StyledBox>
          </CardContent>
        </StyledGrid>
        {image.url && (
          <ImageContainer item lg={4} md={5} xs={12}>
            <StyledCardMedia
              alt={name}
              image={resizedImage}
              title={name}
              focalPointX={image.focal_point_x}
              focalPointY={image.focal_point_y}
            />
            {isSoldOut && (
              <DarkCover>
                <SoldOutTypography
                  align="center"
                  {...itemDescriptionAndDetailsStyles}
                >
                  Sold Out
                </SoldOutTypography>
              </DarkCover>
            )}
            {remainingInventoryIsInCart && (
              <DarkCover>
                <SoldOutTypography
                  align="center"
                  {...itemDescriptionAndDetailsStyles}
                >
                  Last Items In Cart
                </SoldOutTypography>
              </DarkCover>
            )}
          </ImageContainer>
        )}
      </StyledGridContainer>
    </Card>
  );
};

MenuItemCard.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  image: PropTypes.shape({
    url: PropTypes.string,
    focal_point_x: PropTypes.number,
    focal_point_y: PropTypes.number
  }),
  calories: PropTypes.string,
  price: PropTypes.string.isRequired,
  handleClick: PropTypes.func,
  inventory: PropTypes.number,
  inventoryLessCart: PropTypes.number,
  leadTimeText: PropTypes.string,
  minimumQuantity: PropTypes.number,
  quantityHandlingMethod: PropTypes.string,
  variants: PropTypes.array.isRequired,
  lowInventoryCount: PropTypes.number,
  hasInvestorDayFeature: PropTypes.bool.isRequired,
  productHasSurchargeModifier: PropTypes.bool,

  // DIET TYPE OPTIONS
  contains_sugar: PropTypes.bool,
  dairy_free_option: PropTypes.bool,
  halal: PropTypes.bool,
  keto: PropTypes.bool,
  keto_option: PropTypes.bool,
  kosher: PropTypes.bool,
  kosher_option: PropTypes.bool,
  low_fodmap: PropTypes.bool,
  low_sodium: PropTypes.bool,
  non_alcoholic: PropTypes.bool,
  organic: PropTypes.bool,
  organic_option: PropTypes.bool,
  paleo: PropTypes.bool,
  paleo_option: PropTypes.bool,
  plant_based: PropTypes.bool,
  plant_based_option: PropTypes.bool,
  spicy: PropTypes.bool,
  spicy_hot: PropTypes.bool,
  spicy_medium: PropTypes.bool,
  spicy_mild: PropTypes.bool,
  sugar_free: PropTypes.bool,
  uncooked: PropTypes.bool,
  vegan: PropTypes.bool,
  vegan_option: PropTypes.bool,
  vegetarian: PropTypes.bool,
  vegetarian_option: PropTypes.bool,

  // ALLERGEN OPTIONS
  cooked_to_order: PropTypes.bool,
  corn: PropTypes.bool,
  corn_free: PropTypes.bool,
  dairy: PropTypes.bool,
  dairy_free: PropTypes.bool,
  egg: PropTypes.bool,
  egg_free: PropTypes.bool,
  fish: PropTypes.bool,
  fish_free: PropTypes.bool,
  gluten: PropTypes.bool,
  gluten_free: PropTypes.bool,
  nuts: PropTypes.bool,
  nuts_free: PropTypes.bool,
  peanut: PropTypes.bool,
  peanut_free: PropTypes.bool,
  raw: PropTypes.bool,
  raw_egg: PropTypes.bool,
  sesame: PropTypes.bool,
  sesame_free: PropTypes.bool,
  shellfish: PropTypes.bool,
  shellfish_free: PropTypes.bool,
  soy: PropTypes.bool,
  soy_free: PropTypes.bool,
  tree_nut: PropTypes.bool,
  tree_nut_free: PropTypes.bool,
  undercooked: PropTypes.bool,
  wheat: PropTypes.bool,
  wheat_free: PropTypes.bool
};

MenuItemCard.defaultProps = {
  image: {
    url: ''
  },

  quantityHandlingMethod: ''
};

export default MenuItemCard;
